import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import round from '../../../helpers/round';
import MuiSnackbar from '../../common/MuiSnackbar';

const OutputValue = React.memo(({ value, minWidth = 100 }) => {
  const displayValue = value ?? '-';
  return (
    <div className="layeredOutputValue" style={{ minWidth }}>
      {displayValue}
    </div>
  );
});

const OutputLayer = ({
  layer,
  index,
  layers,
  setLayers,
  inputsForSPT,
  resultsObj,
  isLoadingCalculate,
  isLoadingLoad,
  isLoadingSave,
  isExpanded,
  onToggle,
  canUserSeeCalculationSteps,
}) => {
  const regexForPositiveDecimals = /^[+]?\d*(?:[.]\d*)?$/;

  const onChangeWarningForPositiveDecimals = (value, variable, variableSetter) => {
    if (regexForPositiveDecimals.test(value)) {
      variableSetter(value);
    } else {
      showMuiSnack(`${variable}: Can only be a postive number. `, 'warning');
    }
  };

  const updateLayer = useCallback(
    (updates) => {
      setLayers((prev) => {
        const next = prev.slice();
        const current = next[index] || {};
        next[index] = { ...current, ...updates };
        return next;
      });
    },
    [setLayers, index]
  );

  const changeZ = useCallback(
    (value) => {
      updateLayer({ z: value });
    },
    [updateLayer]
  );

  const changeSPT = useCallback(
    (value) => {
      updateLayer({ SPT: value });
    },
    [updateLayer]
  );

  const changeUscs = useCallback(
    (value) => {
      updateLayer({ uscs: value });
    },
    [updateLayer]
  );

  //MUI ALERT
  const [open, setOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [severity, setSeverity] = useState('');

  const showMuiSnack = (message, type) => {
    setAlertMessage(message);
    setSeverity(type);
    setOpen(true);
  };

  const hasResults = Boolean(resultsObj);

  return (
    <>
      <div className="layer sptLayerRow">
        <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} />
        <OutputValue value={layer.id} />
        <TextField
          sx={{
            '& legend': { display: 'none' },
            '& fieldset': { top: 0 },
          }}
          style={{ minWidth: 100 }}
          size="small"
          variant="outlined"
          className="layeredInputMui"
          value={layer.z ?? ''}
          onChange={(e) => onChangeWarningForPositiveDecimals(e.target.value, 'z', changeZ)}
          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
        />
        <OutputValue value={round(resultsObj?.normalStres, 2)} />
        <OutputValue value={round(resultsObj?.effectiveStress, 2)} />
        <TextField
          sx={{
            '& legend': { display: 'none' },
            '& fieldset': { top: 0 },
          }}
          style={{ minWidth: 100 }}
          size="small"
          variant="outlined"
          className="layeredInputMui"
          value={layer.SPT ?? ''}
          onChange={(e) => onChangeWarningForPositiveDecimals(e.target.value, 'SPT', changeSPT)}
          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
        />
        <TextField
          sx={{
            '& legend': { display: 'none' },
            '& fieldset': { top: 0 },
            minWidth: 120,
            maxWidth: 150,
          }}
          size="small"
          select
          className="layeredInputMui"
          value={layer.uscs}
          onChange={(e) => changeUscs(e.target.value)}
          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
        >
          <MenuItem value="0" disabled>
            Select USCS
          </MenuItem>
          <MenuItem value="1">GW</MenuItem>
          <MenuItem value="2">GP</MenuItem>
          <MenuItem value="3">GM</MenuItem>
          <MenuItem value="4">GC</MenuItem>
          <MenuItem value="5">SW</MenuItem>
          <MenuItem value="6">SP</MenuItem>
          <MenuItem value="7">SM</MenuItem>
          <MenuItem value="8">SC</MenuItem>
          <MenuItem value="9">ML</MenuItem>
          <MenuItem value="10">MH</MenuItem>
          <MenuItem value="11">CL</MenuItem>
          <MenuItem value="12">CH</MenuItem>
        </TextField>
        <OutputValue value={round(resultsObj?.siltySandSPT, 2)} />
        <OutputValue value={round(resultsObj?.ce, 2)} />
        <OutputValue value={round(resultsObj?.cb, 2)} />
        <OutputValue value={round(resultsObj?.cs, 2)} />
        <OutputValue value={round(resultsObj?.cr, 2)} />
        <OutputValue value={round(resultsObj?.cn, 2)} />
        <OutputValue value={round(resultsObj?.cbf, 2)} />
        <OutputValue value={round(resultsObj?.n60, 0)} />
        <OutputValue value={round(resultsObj?.n160, 0)} />
        <div className="layeredOutputValue" style={{ minWidth: 80 }}>
          <IconButton size="small" onClick={onToggle} aria-label="Toggle calculation details">
            <ExpandMoreIcon
              fontSize="small"
              style={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s',
              }}
            />
          </IconButton>
        </div>
      </div>
      <Collapse in={isExpanded} unmountOnExit>
        <div className="SPTcalculationDetails">
          {!hasResults ? (
            <div className="sptUpgradeNotice">Run calculation to see details.</div>
          ) : canUserSeeCalculationSteps ? (
            <>
              <div className="sptDetailsTitle">Calculation Details for Layer {layer.id}</div>
              {/* Total Stress at Soil Layer */}
              <div className="sptDetailCard">
                <h4>&sigma; | Total Stress at Soil Layer</h4>
                <p className="sptDetailCardItem">Total stress at a given depth is calculated using:</p>
                <math display="block" className="sptDetailCardMath">
                  <mi>&sigma;</mi>
                  <mo>=</mo>
                  <mi>&gamma;</mi>
                  <mo>&times;</mo>
                  <mi>z</mi>
                </math>
                <p className="sptDetailCardItem">Subsituting the values:</p>
                <math display="block" className="sptDetailCardMath">
                  <mi>&sigma;</mi>
                  <mo>=</mo>
                  <mi>{round(inputsForSPT?.gamma, 2)}</mi>
                  <mo>&times;</mo>
                  <mi>{round(resultsObj?.z, 2)}</mi>
                </math>
                <math display="block">
                  <mi>&sigma;</mi>
                  <mo>=</mo>
                  <mn>{round(resultsObj?.normalStres, 2)}</mn>
                  <mtext>&nbsp;kPa</mtext>
                </math>
              </div>

              {/* Effective Stress at Soil Layer */}
              <div className="sptDetailCard">
                <h4>&sigma;' | Effective Stress at Soil Layer</h4>
                {resultsObj?.gwtLargerThanZ ? (
                  <>
                    <p className="sptDetailCardItem">Since GWT &ge; z, effective stress at a given depth is calculated using:</p>
                    <math display="block" className="sptDetailCardMath">
                      <mi>&sigma;'</mi>
                      <mo>=</mo>
                      <mi>&gamma;</mi>
                      <mo>&times;</mo>
                      <mi>z</mi>
                    </math>
                    <p className="sptDetailCardItem">Subsituting the values:</p>
                    <math display="block" className="sptDetailCardMath">
                      <mi>&sigma;</mi>
                      <mo>=</mo>
                      <mi>{round(inputsForSPT?.gamma, 2)}</mi>
                      <mo>&times;</mo>
                      <mi>{round(resultsObj?.z, 2)}</mi>
                    </math>
                  </>
                ) : (
                  <>
                    <p className="sptDetailCardItem">Since GWT &lt; z, effective stress at a given depth is calculated using:</p>
                    <math display="block" className="sptDetailCardMath">
                      <mi>&sigma;</mi>
                      <mo>=</mo>
                      <mi>&gamma;</mi>
                      <mo>&times;</mo>
                      <mi>GWT</mi>

                      <mo>+</mo>

                      <mo>(</mo>
                      <mi>z</mi>
                      <mo>−</mo>
                      <mi>GWT</mi>
                      <mo>)</mo>

                      <mo>&times;</mo>

                      <mo>(</mo>
                      <mi>&gamma;</mi>
                      <mo>−</mo>
                      <msub>
                        <mi>&gamma;</mi>
                        <mi>w</mi>
                      </msub>
                      <mo>)</mo>
                    </math>

                    <math display="block" className="sptDetailCardMath">
                      <mi>&sigma;</mi>
                      <mo>=</mo>
                      <mi>{round(inputsForSPT?.gamma, 2)}</mi>
                      <mo>&times;</mo>
                      <mi>{round(inputsForSPT?.gwt, 2)}</mi>

                      <mo>+</mo>

                      <mo>(</mo>
                      <mi>{round(resultsObj?.z, 2)}</mi>
                      <mo>−</mo>
                      <mi>{round(inputsForSPT?.gwt, 2)}</mi>
                      <mo>)</mo>

                      <mo>&times;</mo>

                      <mo>(</mo>
                      <mi>{round(inputsForSPT?.gamma, 2)}</mi>
                      <mo>−</mo>
                      <msub>
                        <mi>9.806</mi>
                      </msub>
                      <mo>)</mo>
                    </math>
                  </>
                )}

                <math display="block">
                  <mi>&sigma;</mi>
                  <mo>=</mo>
                  <mn>{round(resultsObj?.effectiveStress, 2)}</mn>
                  <mtext>&nbsp;kPa</mtext>
                </math>
              </div>

              {/* SPT N value after silty sand correction - N' */}
              <div className="sptDetailCard">
                <h4>N' | SPT N value after silty sand correction</h4>
                {resultsObj?.siltySandCorrectionApplied ? (
                  <>
                    <p className="sptDetailCardItem">Since all of the below conditions are present;</p>
                    <p>- The ground water level is above test depth.</p>
                    <p>- The soil is sand or silty sand</p>
                    <p className="sptDetailCardItem">- N &gt;15</p>
                    <p className="sptDetailCardItem">The correction is applied:</p>
                    <math display="block" className="sptDetailCardMath">
                      <msup>
                        <mi>N</mi>
                        <mo>&prime;</mo>
                      </msup>
                      <mo>=</mo>
                      <mn>15</mn>
                      <mo>+</mo>
                      <mn>0.5</mn>
                      <mo>&times;</mo>
                      <mo>(</mo>
                      <mi>N</mi>
                      <mo>−</mo>
                      <mn>15</mn>
                      <mo>)</mo>
                    </math>
                    <math display="block" className="sptDetailCardMath">
                      <msup>
                        <mi>N</mi>
                        <mo>&prime;</mo>
                      </msup>
                      <mo>=</mo>
                      <mn>15</mn>
                      <mo>+</mo>
                      <mn>0.5</mn>
                      <mo>&times;</mo>
                      <mo>(</mo>
                      <mi>{round(resultsObj?.SPT, 2)}</mi>
                      <mo>−</mo>
                      <mn>15</mn>
                      <mo>)</mo>
                    </math>
                    <math display="block" className="sptDetailCardMath">
                      <msup>
                        <mi>N</mi>
                        <mo>&prime;</mo>
                      </msup>
                      <mo>=</mo>
                      <mi>{round(resultsObj?.siltySandSPT, 2)}</mi>
                    </math>
                  </>
                ) : (
                  <>
                    <p className="sptDetailCardItem">Since all of the below conditions are NOT present;</p>
                    <p>- The ground water level is above test depth.</p>
                    <p>- The soil is sand or silty sand</p>
                    <p className="sptDetailCardItem">- N &gt;15</p>
                    <p className="sptDetailCardItem">There is no need for silty sand correction. Therefore;</p>
                    <math display="block" className="sptDetailCardMath">
                      <msup>
                        <mi>N</mi>
                        <mo>&prime;</mo>
                      </msup>
                      <mo>=</mo>
                      <mi>N</mi>
                      <mo>=</mo>
                      <mi>{round(resultsObj?.siltySandSPT, 2)}</mi>
                    </math>
                  </>
                )}
              </div>

              {/* Energy correction factor - Ce */}
              <div className="sptDetailCard">
                <h4>
                  C<sub>e</sub> | Energy correction factor
                </h4>
                <p className="sptDetailCardItem">Energy correction factor is selected based on the hammer type:</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Hammer Type</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        C<sub>E</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Safety</div>
                    <div className="correctionFactorTableColumn">0.60 - 1.17</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Donut</div>
                    <div className="correctionFactorTableColumn">0.45 - 1.00</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">Auto</div>
                    <div className="correctionFactorTableColumn no-border-bottom">0.90 - 1.60</div>
                  </div>
                </div>
                <p className="sptDetailCardItem">
                  Since the hammer type is{' '}
                  {inputsForSPT?.hammerType === 1 ? 'Safety' : inputsForSPT?.hammerType === 2 ? 'Donut' : 'Auto'}, the energy
                  correction factor is used as the average of suggested range, which is:
                </p>
                <math display="block" className="sptDetailCardMath">
                  <msub>
                    <mi>C</mi>
                    <mi>e</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(resultsObj?.ce, 2)}</mi>
                </math>
              </div>

              {/* Borehole diameter correction factor - Cb */}
              <div className="sptDetailCard">
                <h4>
                  C<sub>b</sub> | Borehole diameter correction factor
                </h4>
                <p className="sptDetailCardItem">Borehole diameter correction factor is selected from the table below:</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Borehole Dia.(mm)</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        C<sub>B</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">65 - 115</div>
                    <div className="correctionFactorTableColumn">1.00</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">150</div>
                    <div className="correctionFactorTableColumn">1.05</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">200</div>
                    <div className="correctionFactorTableColumn no-border-bottom">1.15</div>
                  </div>
                </div>
                <p className="sptDetailCardItem">
                  Since the borehole diameter is{' '}
                  {inputsForSPT?.boreholeDiameter === 1
                    ? '65 - 115 mm'
                    : inputsForSPT?.boreholeDiameter === 2
                    ? '150 mm'
                    : '200 mm'}
                  , the selected correction factor is:
                </p>
                <math display="block" className="sptDetailCardMath">
                  <msub>
                    <mi>C</mi>
                    <mi>b</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(resultsObj?.cb, 2)}</mi>
                </math>
              </div>

              {/* Sampler type correction factor - Cs */}
              <div className="sptDetailCard">
                <h4>
                  C<sub>s</sub> | Sampler type correction factor
                </h4>
                <p className="sptDetailCardItem">Sampler type correction factor is selected from the table below:</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Sampler Type</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        C<sub>S</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Standard Liner (With liner)</div>
                    <div className="correctionFactorTableColumn">1.00</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">Without Liner</div>
                    <div className="correctionFactorTableColumn no-border-bottom">1.10 - 1.30</div>
                  </div>
                </div>
                <p className="sptDetailCardItem">
                  {inputsForSPT?.samplerType === 1
                    ? 'Since the sampler type is Standard Liner (With liner), the value from the table is directly used:'
                    : 'Since the sampler type is Without Liner, the correction factor is taken as the average of the suggested range:'}
                </p>
                <math display="block" className="sptDetailCardMath">
                  <msub>
                    <mi>C</mi>
                    <mi>s</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(resultsObj?.cs, 2)}</mi>
                </math>
              </div>

              {/* Rod length correction factor - Cr */}
              <div className="sptDetailCard">
                <h4>
                  C<sub>r</sub> | Rod length correction factor
                </h4>
                <p className="sptDetailCardItem">Rod length correction factor is selected from the table below:</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Rod Length (m)</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        C<sub>R</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">0 - 4</div>
                    <div className="correctionFactorTableColumn">0.75</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">4 - 6</div>
                    <div className="correctionFactorTableColumn">0.85</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">6 - 10</div>
                    <div className="correctionFactorTableColumn">0.95</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">&gt; 10</div>
                    <div className="correctionFactorTableColumn no-border-bottom">1.00</div>
                  </div>
                </div>
                <p className="sptDetailCardItem">The selected correction factor is:</p>
                <math display="block" className="sptDetailCardMath">
                  <msub>
                    <mi>C</mi>
                    <mi>r</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(resultsObj?.cr, 2)}</mi>
                </math>
              </div>

              {/* Overburden correction factor - Cn */}
              <div className="sptDetailCard">
                <h4>
                  C<sub>n</sub> | Overburden pressure correction factor
                </h4>
                {resultsObj?.CnNotApplicableDueCohesion ? (
                  <>
                    <p className="sptDetailCardItem">
                      Since the soil is cohesive C<sub>n</sub> is not applicable and is taken as 1.0.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="sptDetailCardItem">Overburden pressure correction factor is calculated using:</p>
                    <math display="block" className="sptDetailCardMath">
                      <msub>
                        <mi>C</mi>
                        <mi>n</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>9.78</mn>
                      <mo>&times;</mo>
                      <msup>
                        <mrow>
                          <mo>(</mo>
                          <mfrac>
                            <mn>1</mn>
                            <msup>
                              <mi>&sigma;</mi>
                              <mo>&prime;</mo>
                            </msup>
                          </mfrac>
                          <mo>)</mo>
                        </mrow>
                        <mn>0.5</mn>
                      </msup>
                    </math>
                    <p className="sptDetailCardItem">Substituting the values:</p>
                    <math display="block" className="sptDetailCardMath">
                      <msub>
                        <mi>C</mi>
                        <mi>n</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>9.78</mn>
                      <mo>&times;</mo>
                      <msup>
                        <mrow>
                          <mo>(</mo>
                          <mfrac>
                            <mn>1</mn>
                            <mn>{round(resultsObj?.effectiveStress, 2)}</mn>
                          </mfrac>
                          <mo>)</mo>
                        </mrow>
                        <mn>0.5</mn>
                      </msup>
                    </math>
                  </>
                )}
                {resultsObj?.CnCappedBottom ? (
                  <p className="sptDetailCardItem">
                    The calculated value is below 0.5, so C<sub>n</sub> is capped at 0.5.
                  </p>
                ) : null}
                {resultsObj?.CnCappedTop ? (
                  <p className="sptDetailCardItem">
                    The calculated value is above 1.7, so C<sub>n</sub> is capped at 1.7.
                  </p>
                ) : null}
                <math display="block" className="sptDetailCardMath">
                  <msub>
                    <mi>C</mi>
                    <mi>n</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(resultsObj?.cn, 2)}</mi>
                </math>
              </div>

              {/* Blow count frequency correction factor - Cbf */}
              <div className="sptDetailCard">
                <h4>
                  C<sub>BF</sub> | Blow count frequency correction factor
                </h4>
                <p className="sptDetailCardItem">Blow count frequency correction factor is selected from the table below:</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Blow Count</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        C<sub>BF</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">&lt; 20</div>
                    <div className="correctionFactorTableColumn">0.95</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">&gt; 20</div>
                    <div className="correctionFactorTableColumn no-border-bottom">1.05</div>
                  </div>
                </div>
                <p className="sptDetailCardItem">The selected correction factor is:</p>
                <math display="block" className="sptDetailCardMath">
                  <msub>
                    <mi>C</mi>
                    <mi>BF</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(resultsObj?.cbf, 2)}</mi>
                </math>
              </div>

              {/* N60 */}
              <div className="sptDetailCard">
                <h4>
                  (N)<sub>60</sub> | Corrected SPT value
                </h4>
                <p className="sptDetailCardItem">Calculated using:</p>
                <math display="block" className="sptDetailCardMath">
                  <msub>
                    <mi>N</mi>
                    <mi>60</mi>
                  </msub>
                  <mo>=</mo>
                  <msup>
                    <mi>N</mi>
                    <mo>&prime;</mo>
                  </msup>
                  <mo>&times;</mo>
                  <msub>
                    <mi>C</mi>
                    <mi>e</mi>
                  </msub>
                  <mo>&times;</mo>
                  <msub>
                    <mi>C</mi>
                    <mi>b</mi>
                  </msub>
                  <mo>&times;</mo>
                  <msub>
                    <mi>C</mi>
                    <mi>s</mi>
                  </msub>
                  <mo>&times;</mo>
                  <msub>
                    <mi>C</mi>
                    <mi>r</mi>
                  </msub>
                  <mo>&times;</mo>
                  <msub>
                    <mi>C</mi>
                    <mi>BF</mi>
                  </msub>
                </math>
                <p className="sptDetailCardItem">Substituting the values:</p>
                <math display="block" className="sptDetailCardMath">
                  <msub>
                    <mi>N</mi>
                    <mi>60</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(resultsObj?.siltySandSPT, 2)}</mi>
                  <mo>&times;</mo>
                  <mi>{round(resultsObj?.ce, 2)}</mi>
                  <mo>&times;</mo>
                  <mi>{round(resultsObj?.cb, 2)}</mi>
                  <mo>&times;</mo>
                  <mi>{round(resultsObj?.cs, 2)}</mi>
                  <mo>&times;</mo>
                  <mi>{round(resultsObj?.cr, 2)}</mi>
                  <mo>&times;</mo>
                  <mi>{round(resultsObj?.cbf, 2)}</mi>
                </math>
                <math display="block" className="sptDetailCardMath">
                  <msub>
                    <mi>N</mi>
                    <mi>60</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(resultsObj?.n60, 0)}</mi>
                </math>
              </div>

              {/* N160 */}
              <div className="sptDetailCard">
                <h4>
                  (N)<sub>1</sub>
                  <sub>60</sub> | Overburden-corrected SPT value
                </h4>
                <p className="sptDetailCardItem">Calculated using:</p>
                <math display="block" className="sptDetailCardMath">
                  <msub>
                    <mi>N</mi>
                    <mrow>
                      <mn>1</mn>
                      <mn>60</mn>
                    </mrow>
                  </msub>
                  <mo>=</mo>
                  <msub>
                    <mi>C</mi>
                    <mi>n</mi>
                  </msub>
                  <mo>&times;</mo>
                  <msub>
                    <mi>N</mi>
                    <mi>60</mi>
                  </msub>
                </math>
                <p className="sptDetailCardItem">Substituting the values:</p>
                <math display="block" className="sptDetailCardMath">
                  <msub>
                    <mi>N</mi>
                    <mrow>
                      <mn>1</mn>
                      <mn>60</mn>
                    </mrow>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(resultsObj?.cn, 2)}</mi>
                  <mo>&times;</mo>
                  <mi>{round(resultsObj?.n60, 0)}</mi>
                </math>
                <math display="block" className="sptDetailCardMath">
                  <msub>
                    <mi>N</mi>
                    <mrow>
                      <mn>1</mn>
                      <mn>60</mn>
                    </mrow>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(resultsObj?.n160, 0)}</mi>
                </math>
              </div>
            </>
          ) : (
            <div className="sptUpgradeNotice">
              <LockOutlinedIcon fontSize="small" />
              <span>
                Upgrade to view calculation details.{' '}
                <Link to="/pricing" className="sptUpgradeLink" target="_blank" rel="noreferrer">
                  See plans
                </Link>
                .
              </span>
            </div>
          )}
        </div>
      </Collapse>
    </>
  );
};

export default React.memo(OutputLayer);
